import React, { useState } from 'react';
import { EXTRA_KINDS } from '../model';
import { ImageUpload } from '../../components/ImageUpload';

const fileToDataUrl = (file, cb) => { if (!file) return; const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };
const linesToArr = s => s.split('\n');
const arrToLines = a => (a || []).join('\n');

export function Section({ title, hint, children, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={'form-section' + (open ? '' : ' collapsed')} data-testid={testid}>
      <button type="button" className="section-head" data-testid={testid + '-toggle'} aria-expanded={open} onClick={() => setOpen(o => !o)}><span className={'caret' + (open ? ' open' : '')}>▸</span>{title}</button>
      {open && <div className="section-body">{hint && <p className="field-hint" style={{ margin: '0 0 8px' }}>{hint}</p>}{children}</div>}
    </div>
  );
}

export function Field({ label, hint, children }) {
  return <div className="field"><label className="field-label">{label}{hint && <span className="field-hint"> {hint}</span>}</label>{children}</div>;
}

export function Text({ label, hint, value, onChange, placeholder, testid, readOnly = false }) {
  return <Field label={label} hint={hint}><input className="input" data-testid={testid} value={value || ''} readOnly={readOnly} onChange={onChange ? e => onChange(e.target.value) : undefined} placeholder={placeholder} /></Field>;
}

// Single-line twin of DraftArea — for inputs whose text is parsed on each keystroke.
export function DraftText({ label, hint, text, onText, placeholder, testid }) {
  const [draft, setDraft] = useState(null);
  return <Field label={label} hint={hint}><input className="input" data-testid={testid}
    value={draft == null ? (text || '') : draft}
    onChange={e => { setDraft(e.target.value); onText(e.target.value); }}
    onBlur={() => setDraft(null)} placeholder={placeholder} /></Field>;
}

export function Area({ label, hint, value, onChange, placeholder, rows = 3, testid }) {
  return <Field label={label} hint={hint}><textarea className="textarea" rows={rows} data-testid={testid} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} /></Field>;
}

// Textarea whose text is parsed into structured data on every keystroke. The raw
// text is kept locally while editing so trailing spaces and half-typed lines are
// not scrubbed out from under the cursor.
export function DraftArea({ label, hint, text, onText, placeholder, rows = 3, testid }) {
  const [draft, setDraft] = useState(null);
  return <Field label={label} hint={hint}><textarea className="textarea" rows={rows} data-testid={testid}
    value={draft == null ? (text || '') : draft}
    onChange={e => { setDraft(e.target.value); onText(e.target.value); }}
    onBlur={() => setDraft(null)} placeholder={placeholder} /></Field>;
}

// string[] edited as one-per-line text.
export function Lines({ label, hint, items, onChange, placeholder, rows = 3, testid }) {
  return <Area label={label} hint={hint || '(one per line)'} rows={rows} testid={testid} value={arrToLines(items)} onChange={v => onChange(linesToArr(v))} placeholder={placeholder} />;
}

// string[][] edited as "a | b | c" per line.
export function PipeRows({ label, hint, rows, onChange, placeholder, testid }) {
  const val = (rows || []).map(r => (r || []).join(' | ')).join('\n');
  return <DraftArea label={label} hint={hint || '(one row per line, cells separated by |)'} rows={4} testid={testid} text={val} onText={v => onChange(v.split('\n').map(l => l.split('|').map(c => c.trim())))} placeholder={placeholder} />;
}

export function Toggle({ options, value, onChange, testid }) {
  return (
    <div className="us-toggle" data-testid={testid}>
      {options.map(o => (
        <button type="button" key={o.value} className={'us-toggle-btn' + (value === o.value ? ' on' : '')} data-testid={testid + '-' + o.value} onClick={() => onChange(o.value)}>
          {o.label}{o.pages && <span className="us-toggle-sub">{o.pages}</span>}
        </button>
      ))}
    </div>
  );
}

export function Check({ label, checked, onChange, testid }) {
  return <label className="us-check" data-testid={testid}><input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} /> {label}</label>;
}

export function Img({ label, url, onChange, testid, inline }) {
  const body = <ImageUpload url={url} onFile={f => fileToDataUrl(f, onChange)} onClear={() => onChange('')} testid={testid} inline={inline} />;
  return label ? <Field label={label}>{body}</Field> : body;
}

// Generic list-of-objects editor. cols: [{ key, placeholder, type: 'text'|'select'|'number', options, width }]
export function Rows({ label, hint, rows, onChange, cols, blank, testid, addLabel = 'Add row', max }) {
  const list = rows || [];
  const set = (i, k, v) => onChange(list.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const a = [...list]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  const tpl = cols.map(c => c.width || '1fr').join(' ') + ' auto';
  return (
    <div className="field" data-testid={testid}>
      {label && <label className="field-label">{label}{hint && <span className="field-hint"> {hint}</span>}</label>}
      {list.map((r, i) => (
        <div className="us-row" key={i} style={{ gridTemplateColumns: tpl }} data-testid={`${testid}-${i}`}>
          {cols.map(c => c.type === 'select' ? (
            <select key={c.key} className="input" data-testid={`${testid}-${i}-${c.key}`} value={r[c.key] || ''} onChange={e => set(i, c.key, e.target.value)}>
              {c.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input key={c.key} className="input" data-testid={`${testid}-${i}-${c.key}`} value={r[c.key] == null ? '' : r[c.key]} placeholder={c.placeholder} onChange={e => set(i, c.key, e.target.value)} />
          ))}
          <span className="us-row-actions">
            <button className="btn btn-sm" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">↑</button>
            <button className="btn btn-sm" disabled={i === list.length - 1} onClick={() => move(i, 1)} title="Move down">↓</button>
            <button className="btn btn-sm btn-danger" data-testid={`${testid}-del-${i}`} onClick={() => del(i)} title="Remove">✕</button>
          </span>
        </div>
      ))}
      {(!max || list.length < max) && <button className="btn btn-sm" data-testid={`${testid}-add`} onClick={() => onChange([...list, { ...(blank || {}) }])}>＋ {addLabel}</button>}
    </div>
  );
}

export const KV_COLS = [{ key: 'label', placeholder: 'Label' }, { key: 'value', placeholder: 'Value' }];

// Custom blocks that can be dropped on any page.
export function ExtrasEditor({ blocks, onChange, testid }) {
  const list = blocks || [];
  const set = (i, patch) => onChange(list.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  return (
    <div className="us-extras" data-testid={testid}>
      <label className="field-label">Custom fields on this page</label>
      {list.map((b, i) => (
        <div className="us-card" key={i} data-testid={`${testid}-${i}`}>
          <div className="us-card-head">
            <input className="input" placeholder="Block title (optional)" data-testid={`${testid}-${i}-title`} value={b.title || ''} onChange={e => set(i, { title: e.target.value })} />
            <select className="input" data-testid={`${testid}-${i}-kind`} value={b.kind || 'rows'} onChange={e => set(i, { kind: e.target.value })}>{EXTRA_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}</select>
            <button className="btn btn-sm btn-danger" data-testid={`${testid}-del-${i}`} onClick={() => onChange(list.filter((_, idx) => idx !== i))}>✕</button>
          </div>
          {(b.kind || 'rows') === 'rows' && <Rows rows={b.rows} onChange={rows => set(i, { rows })} cols={KV_COLS} blank={{ label: '', value: '' }} testid={`${testid}-${i}-rows`} />}
          {b.kind === 'bullets' && <Lines items={b.items} onChange={items => set(i, { items })} placeholder="One bullet per line" testid={`${testid}-${i}-items`} />}
          {b.kind === 'text' && <Area value={b.text} onChange={text => set(i, { text })} placeholder="Paragraph text" testid={`${testid}-${i}-text`} />}
        </div>
      ))}
      <button className="btn btn-sm" data-testid={`${testid}-add`} onClick={() => onChange([...list, { title: '', kind: 'rows', rows: [], items: [], text: '' }])}>＋ Add custom field block</button>
    </div>
  );
}
